/**
 * Centralized Logging and Monitoring Utility
 * Provides structured logging with levels, contexts, and performance tracking
 */

// ============================================================
// TYPES
// ============================================================

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'performance';

export interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  category: string;
  message: string;
  data?: Record<string, unknown>;
  stack?: string;
  performanceMetrics?: PerformanceMetrics;
}

export interface PerformanceMetrics {
  duration?: number;
  memoryUsed?: number;
  operationCount?: number;
  [key: string]: number | undefined;
}

export interface LoggerConfig {
  enabled: boolean;
  minLevel: LogLevel;
  includeTimestamps: boolean;
  includeStackTraces: boolean;
  maxStoredLogs: number;
  persistToLocalStorage: boolean;
  consoleOutput: boolean;
  structuredJsonOutput: boolean;
  fileOutput: boolean;
  fileOutputFormat: 'json' | 'jsonl';
  productionMode: boolean;
}

// ============================================================
// LOG CATEGORIES
// ============================================================

export const LOG_CATEGORIES = {
  // Core System
  APP: 'App',
  INIT: 'Initialization',

  // Context
  NETWORK: 'Network',
  CANVAS: 'Canvas',
  PYODIDE: 'Pyodide',

  // Features
  POWER_FLOW: 'PowerFlow',
  EXPORT: 'Export',
  IMPORT: 'Import',
  HISTORY: 'History',

  // Interactions
  USER_ACTION: 'UserAction',
  SELECTION: 'Selection',
  NODE_OPS: 'NodeOperations',
  CONNECTION_OPS: 'ConnectionOperations',

  // Performance
  PERFORMANCE: 'Performance',
  RENDER: 'Render',

  // Code generation
  CODEGEN: 'CodeGen',

  // Storage
  PERSISTENCE: 'Persistence',

  // Errors
  ERROR: 'Error',
  VALIDATION: 'Validation',
} as const;

// ============================================================
// LOG LEVELS
// ============================================================

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  performance: 1,
};

const isProd = import.meta.env.PROD;

// ============================================================
// LOGGER CLASS
// ============================================================

class Logger {
  private config: LoggerConfig;
  private logs: LogEntry[] = [];
  private performanceMarks: Map<string, number> = new Map();
  private fileBuffer: string[] = [];

  constructor(config?: Partial<LoggerConfig>) {
    this.config = {
      enabled: true,
      minLevel: isProd ? 'info' : 'debug',
      includeTimestamps: true,
      includeStackTraces: !isProd,
      maxStoredLogs: 1000,
      persistToLocalStorage: false,
      consoleOutput: true,
      structuredJsonOutput: true,
      fileOutput: true,
      fileOutputFormat: 'jsonl',
      productionMode: isProd,
      ...config,
    };

    // Load persisted logs if enabled
    if (this.config.persistToLocalStorage) {
      this.loadPersistedLogs();
    }
  }

  // ============================================================
  // CORE LOGGING METHODS
  // ============================================================

  debug(category: string, message: string, data?: Record<string, unknown>): void {
    this.log('debug', category, message, data);
  }

  info(category: string, message: string, data?: Record<string, unknown>): void {
    this.log('info', category, message, data);
  }

  warn(category: string, message: string, data?: Record<string, unknown>): void {
    this.log('warn', category, message, data);
  }

  error(category: string, message: string, error?: Error | unknown, data?: Record<string, unknown>): void {
    const errorData = error instanceof Error
      ? {
          name: error.name,
          message: error.message,
          stack: error.stack,
          ...data,
        }
      : { error, ...data };

    this.log('error', category, message, errorData, error instanceof Error ? error.stack : undefined);
  }

  performance(category: string, message: string, metrics: PerformanceMetrics): void {
    this.log('performance', category, message, undefined, undefined, metrics);
  }

  // ============================================================
  // MAIN LOG METHOD
  // ============================================================

  private log(
    level: LogLevel,
    category: string,
    message: string,
    data?: Record<string, unknown>,
    stack?: string,
    performanceMetrics?: PerformanceMetrics
  ): void {
    if (!this.config.enabled) return;

    // Check log level
    if (LOG_LEVEL_PRIORITY[level] < LOG_LEVEL_PRIORITY[this.config.minLevel]) {
      return;
    }

    const entry: LogEntry = {
      timestamp: new Date(),
      level,
      category,
      message,
      data,
      stack: this.config.includeStackTraces ? stack : undefined,
      performanceMetrics,
    };

    const serialized = this.serializeEntry(entry);

    // Store log entry
    this.logs.push(entry);

    // Trim logs if exceeding max
    if (this.logs.length > this.config.maxStoredLogs) {
      this.logs = this.logs.slice(-this.config.maxStoredLogs);
    }

    if (this.config.fileOutput && this.config.fileOutputFormat === 'jsonl') {
      this.fileBuffer.push(JSON.stringify(serialized));
      if (this.fileBuffer.length > this.config.maxStoredLogs) {
        this.fileBuffer = this.fileBuffer.slice(-this.config.maxStoredLogs);
      }
    }

    // Persist if enabled
    if (this.config.persistToLocalStorage) {
      this.persistLogs();
    }

    // Console output
    if (this.config.consoleOutput) {
      this.outputToConsole(entry);
    }

    if (this.config.structuredJsonOutput) {
      this.outputStructuredJson(serialized);
    }
  }

  // ============================================================
  // PERFORMANCE TRACKING
  // ============================================================

  /**
   * Start a performance measurement
   */
  startPerformance(operationId: string): void {
    this.performanceMarks.set(operationId, performance.now());
  }

  /**
   * End a performance measurement and log it
   */
  endPerformance(operationId: string, category: string, message: string, additionalMetrics?: PerformanceMetrics): void {
    const startTime = this.performanceMarks.get(operationId);
    if (!startTime) {
      this.warn(LOG_CATEGORIES.PERFORMANCE, `No start mark found for operation: ${operationId}`);
      return;
    }

    const duration = performance.now() - startTime;
    this.performanceMarks.delete(operationId);

    const metrics: PerformanceMetrics = {
      duration,
      ...additionalMetrics,
    };

    // Add memory info if available
    if ((performance as any).memory) {
      metrics.memoryUsed = (performance as any).memory.usedJSHeapSize;
    }

    this.performance(category, message, metrics);
  }

  /**
   * Measure execution time of a function
   */
  async measureAsync<T>(
    operationId: string,
    category: string,
    operation: () => Promise<T>
  ): Promise<T> {
    this.startPerformance(operationId);
    try {
      const result = await operation();
      this.endPerformance(operationId, category, `Async operation: ${operationId}`);
      return result;
    } catch (error) {
      this.endPerformance(operationId, category, `Async operation failed: ${operationId}`);
      throw error;
    }
  }

  /**
   * Measure execution time of a synchronous function
   */
  measure<T>(operationId: string, category: string, operation: () => T): T {
    this.startPerformance(operationId);
    try {
      const result = operation();
      this.endPerformance(operationId, category, `Sync operation: ${operationId}`);
      return result;
    } catch (error) {
      this.endPerformance(operationId, category, `Sync operation failed: ${operationId}`);
      throw error;
    }
  }

  // ============================================================
  // USER ACTION TRACKING
  // ============================================================

  /**
   * Log user actions for analytics and debugging
   */
  logUserAction(action: string, data?: Record<string, unknown>): void {
    this.info(LOG_CATEGORIES.USER_ACTION, action, {
      timestamp: new Date().toISOString(),
      ...data,
    });
  }

  /**
   * Log network state changes
   */
  logNetworkChange(operation: string, details: Record<string, unknown>): void {
    this.info(LOG_CATEGORIES.NETWORK, `Network ${operation}`, details);
  }

  /**
   * Log validation errors
   */
  logValidationError(context: string, errors: string[]): void {
    this.warn(LOG_CATEGORIES.VALIDATION, `Validation failed: ${context}`, { errors });
  }

  // ============================================================
  // CONSOLE OUTPUT
  // ============================================================

  private outputToConsole(entry: LogEntry): void {
    const prefix = `[${entry.timestamp.toISOString()}] [${entry.category}]`;
    const message = `${prefix} ${entry.message}`;

    switch (entry.level) {
      case 'debug':
        console.debug(message, entry.data || '');
        break;
      case 'info':
        console.info(message, entry.data || '');
        break;
      case 'warn':
        console.warn(message, entry.data || '');
        break;
      case 'error':
        console.error(message, entry.data || '', entry.stack || '');
        break;
      case 'performance':
        console.log(
          `%c${message}`,
          'color: #8b5cf6; font-weight: bold',
          entry.performanceMetrics || ''
        );
        break;
    }
  }

  private outputStructuredJson(serialized: Record<string, unknown>): void {
    try {
      console.log(JSON.stringify(serialized));
    } catch (error) {
      console.warn('Failed to output structured log:', error);
    }
  }

  // ============================================================
  // PERSISTENCE
  // ============================================================

  private persistLogs(): void {
    try {
      const recentLogs = this.logs.slice(-100); // Only persist last 100 logs
      localStorage.setItem('MangoGrid_logs', JSON.stringify(recentLogs));
    } catch (error) {
      // Ignore localStorage errors
      console.warn('Failed to persist logs:', error);
    }
  }

  private loadPersistedLogs(): void {
    try {
      const stored = localStorage.getItem('MangoGrid_logs');
      if (stored) {
        const parsed = JSON.parse(stored);
        this.logs = parsed.map((log: any) => ({
          ...log,
          timestamp: new Date(log.timestamp),
        }));
      }
    } catch (error) {
      console.warn('Failed to load persisted logs:', error);
    }
  }

  // ============================================================
  // LOG RETRIEVAL
  // ============================================================

  /**
   * Get all logs
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Get logs by level
   */
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter((log) => log.level === level);
  }

  /**
   * Get logs by category
   */
  getLogsByCategory(category: string): LogEntry[] {
    return this.logs.filter((log) => log.category === category);
  }

  /**
   * Get logs within time range
   */
  getLogsByTimeRange(start: Date, end: Date): LogEntry[] {
    return this.logs.filter(
      (log) => log.timestamp >= start && log.timestamp <= end
    );
  }

  /**
   * Get error logs
   */
  getErrors(): LogEntry[] {
    return this.getLogsByLevel('error');
  }

  /**
   * Get performance logs
   */
  getPerformanceLogs(): LogEntry[] {
    return this.getLogsByLevel('performance');
  }

  /**
   * Export logs as JSON
   */
  exportLogs(): string {
    return JSON.stringify(this.logs.map((log) => this.serializeEntry(log)), null, 2);
  }

  /**
   * Export logs as JSON Lines
   */
  exportLogsJsonLines(): string {
    if (this.fileBuffer.length > 0) {
      return this.fileBuffer.join('\n');
    }
    return this.logs.map((log) => JSON.stringify(this.serializeEntry(log))).join('\n');
  }

  /**
   * Download logs as a file (json or jsonl)
   */
  downloadLogs(format: 'json' | 'jsonl' = 'json', filename?: string): void {
    if (typeof document === 'undefined') return;
    const content = format === 'jsonl' ? this.exportLogsJsonLines() : this.exportLogs();
    const blob = new Blob([content], { type: format === 'jsonl' ? 'application/jsonl' : 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `MangoGrid-logs-${new Date().toISOString()}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
    this.fileBuffer = [];
    if (this.config.persistToLocalStorage) {
      localStorage.removeItem('MangoGrid_logs');
    }
  }

  private serializeEntry(entry: LogEntry): Record<string, unknown> {
    return {
      timestamp: entry.timestamp.toISOString(),
      level: entry.level,
      category: entry.category,
      message: entry.message,
      data: entry.data,
      stack: entry.stack,
      performanceMetrics: entry.performanceMetrics,
    };
  }

  // ============================================================
  // CONFIGURATION
  // ============================================================

  /**
   * Update logger configuration
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current configuration
   */
  getConfig(): LoggerConfig {
    return { ...this.config };
  }

  // ============================================================
  // STATISTICS
  // ============================================================

  /**
   * Get logging statistics
   */
  getStats(): {
    totalLogs: number;
    byLevel: Record<LogLevel, number>;
    byCategory: Record<string, number>;
    errors: number;
    averagePerformance?: number;
  } {
    const byLevel: Record<LogLevel, number> = {
      debug: 0,
      info: 0,
      warn: 0,
      error: 0,
      performance: 0,
    };

    const byCategory: Record<string, number> = {};

    let totalPerformanceDuration = 0;
    let performanceCount = 0;

    this.logs.forEach((log) => {
      byLevel[log.level]++;
      byCategory[log.category] = (byCategory[log.category] || 0) + 1;

      if (log.performanceMetrics?.duration) {
        totalPerformanceDuration += log.performanceMetrics.duration;
        performanceCount++;
      }
    });

    return {
      totalLogs: this.logs.length,
      byLevel,
      byCategory,
      errors: byLevel.error,
      averagePerformance: performanceCount > 0 ? totalPerformanceDuration / performanceCount : undefined,
    };
  }
}

// ============================================================
// SINGLETON INSTANCE
// ============================================================

export const logger = new Logger({
  enabled: true,
  minLevel: isProd ? 'info' : 'debug',
  consoleOutput: true,
  persistToLocalStorage: false,
  productionMode: isProd,
});

// ============================================================
// CONVENIENCE EXPORTS
// ============================================================

export const log = {
  debug: logger.debug.bind(logger),
  info: logger.info.bind(logger),
  warn: logger.warn.bind(logger),
  error: logger.error.bind(logger),
  performance: logger.performance.bind(logger),
  userAction: logger.logUserAction.bind(logger),
  networkChange: logger.logNetworkChange.bind(logger),
  validationError: logger.logValidationError.bind(logger),
  startPerf: logger.startPerformance.bind(logger),
  endPerf: logger.endPerformance.bind(logger),
  measure: logger.measure.bind(logger),
  measureAsync: logger.measureAsync.bind(logger),
};

export default logger;

